import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Send, Mail, Users, Store as StoreIcon, UserPlus, Sparkles, Clock, X } from 'lucide-react';
import { API } from '../App';

const AUDIENCES = [
  { value: 'all_users', label: 'All users (customers + vendors)', icon: Users },
  { value: 'customers', label: 'Customers only', icon: UserPlus },
  { value: 'vendors', label: 'Vendors only', icon: StoreIcon },
  { value: 'leads', label: 'Email capture leads', icon: Sparkles },
  { value: 'custom', label: 'Custom list (paste emails)', icon: Mail },
];

// Quick-pick presets for scheduling — all returned as local-datetime strings
// suitable for an <input type="datetime-local">.
function toLocalInputValue(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function makePresets() {
  const now = new Date();
  const in1h = new Date(now.getTime() + 60 * 60 * 1000);
  const tomorrow9 = new Date(now); tomorrow9.setDate(now.getDate() + 1); tomorrow9.setHours(9, 0, 0, 0);
  const nextSunday = new Date(now);
  const daysUntilSunday = (7 - now.getDay()) % 7 || 7;
  nextSunday.setDate(now.getDate() + daysUntilSunday); nextSunday.setHours(9, 0, 0, 0);
  const year = now.getFullYear();
  // Fallback holiday pickers if those dates are still ahead this year, else next year.
  const easterApprox = new Date(year, 3, 5, 9, 0, 0); // April 5 ~ Easter window
  if (easterApprox < now) easterApprox.setFullYear(year + 1);
  const bfriday = new Date(year, 10, 27, 9, 0, 0); // last-Fri-Nov approx
  if (bfriday < now) bfriday.setFullYear(year + 1);
  const christmas = new Date(year, 11, 24, 9, 0, 0);
  if (christmas < now) christmas.setFullYear(year + 1);
  return [
    { label: 'In 1 hour', value: toLocalInputValue(in1h) },
    { label: 'Tomorrow 9am', value: toLocalInputValue(tomorrow9) },
    { label: 'Next Sunday 9am', value: toLocalInputValue(nextSunday) },
    { label: 'Easter morning', value: toLocalInputValue(easterApprox) },
    { label: 'Black Friday 9am', value: toLocalInputValue(bfriday) },
    { label: 'Christmas Eve 9am', value: toLocalInputValue(christmas) },
  ];
}

const TEMPLATES = [
  {
    key: 'newsletter',
    label: 'Monthly newsletter',
    subject: 'This month at Christ Kingdom Global Marketplace',
    body: `<p>Hi friend,</p>
<p>Here&rsquo;s what&rsquo;s new at Christ Kingdom Global Marketplace this month:</p>
<ul>
  <li><strong>New vendors joined</strong> — [list a few names]</li>
  <li><strong>Featured story</strong> — [paste link]</li>
  <li><strong>Deal of the month</strong> — [paste product link]</li>
</ul>
<p>Blessings,<br/>The CKGM Team</p>`,
  },
  {
    key: 'flash_sale',
    label: 'Flash sale / promotion',
    subject: 'Flash sale: 24 hours only',
    body: `<p>For the next 24 hours, save on hand-picked items from our verified Christian vendors.</p>
<p><a href="https://christkingdomglobalmarketplace.com/products?utm_source=email&utm_medium=broadcast&utm_campaign=flash_sale">Shop the sale now →</a></p>`,
  },
  {
    key: 'vendor_recruit',
    label: 'Vendor recruitment',
    subject: 'Sell on Christ Kingdom Global Marketplace — keep 90%',
    body: `<p>We&rsquo;re opening applications for founding vendors.</p>
<ul>
  <li>Flat 10% platform fee — you keep 90%</li>
  <li>Stripe Connect payouts (no PayPal disputes)</li>
  <li>Global buyer base across 100+ countries</li>
</ul>
<p><a href="https://christkingdomglobalmarketplace.com/vendor/apply?utm_source=email&utm_medium=broadcast&utm_campaign=vendor_recruit">Apply now →</a></p>`,
  },
];

export default function EmailBroadcastAdmin() {
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [audience, setAudience] = useState('all_users');
  const [customEmails, setCustomEmails] = useState('');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [scheduleAt, setScheduleAt] = useState('');  // local datetime string, '' = send now

  const presets = React.useMemo(() => makePresets(), []);

  const loadHistory = useCallback(async () => {
    try {
      setLoadingHistory(true);
      const r = await axios.get(`${API}/admin/broadcasts`);
      setHistory(r.data?.broadcasts || []);
    } catch (_e) {
      // silent — history is nice-to-have
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const applyTemplate = (key) => {
    const t = TEMPLATES.find((x) => x.key === key);
    if (!t) return;
    setSubject(t.subject);
    setBodyHtml(t.body);
    toast.success(`Template "${t.label}" loaded — edit before sending.`);
  };

  const handleCancel = async (broadcastId) => {
    if (!window.confirm('Cancel this scheduled broadcast? Queued emails will not be sent.')) return;
    try {
      await axios.post(`${API}/admin/broadcasts/${broadcastId}/cancel`);
      toast.success('Scheduled broadcast cancelled.');
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel broadcast.');
    }
  };

  const handleSend = async () => {
    if (!subject.trim() || !bodyHtml.trim()) {
      toast.error('Subject and body are required.');
      return;
    }
    const emails = audience === 'custom'
      ? customEmails.split(/[\s,;]+/).map((e) => e.trim()).filter((e) => e.includes('@'))
      : [];
    if (audience === 'custom' && emails.length === 0) {
      toast.error('Paste at least one valid email in the custom list.');
      return;
    }

    // Parse schedule — only pass to backend if the picked moment is >=1 min in the future.
    let sendAtIso = null;
    if (scheduleAt) {
      const scheduledDate = new Date(scheduleAt);
      if (isNaN(scheduledDate.getTime())) {
        toast.error('Invalid schedule time.');
        return;
      }
      if (scheduledDate.getTime() <= Date.now() + 60_000) {
        toast.error('Schedule time must be at least 1 minute in the future (or clear it to send now).');
        return;
      }
      sendAtIso = scheduledDate.toISOString();
    }

    const audienceLabel = AUDIENCES.find((a) => a.value === audience)?.label;
    const scheduleLabel = sendAtIso
      ? ` on ${new Date(sendAtIso).toLocaleString()}`
      : ' now';
    const confirmMsg = audience === 'custom'
      ? `Deliver this broadcast to ${emails.length} custom recipient(s)${scheduleLabel}?`
      : `Deliver this broadcast to the "${audienceLabel}" list${scheduleLabel}?`;
    if (!window.confirm(confirmMsg)) return;

    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/broadcasts/send`, {
        subject: subject.trim(),
        body_html: bodyHtml.trim(),
        audience,
        ...(audience === 'custom' ? { emails } : {}),
        ...(sendAtIso ? { send_at: sendAtIso } : {}),
      });
      toast.success(r.data?.message || 'Broadcast queued.');
      setSubject('');
      setBodyHtml('');
      setCustomEmails('');
      setScheduleAt('');
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send broadcast.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="broadcast-admin">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" /> Email Broadcast Composer
          </CardTitle>
          <CardDescription>
            Send a one-off announcement, newsletter, or promo email to your list.
            Delivered via Resend. Please keep it faith-honoring and compliant with CAN-SPAM.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Template picker */}
          <div>
            <label className="text-sm font-medium block mb-2">Start from a template (optional)</label>
            <div className="flex flex-wrap gap-2">
              {TEMPLATES.map((t) => (
                <Button
                  key={t.key}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => applyTemplate(t.key)}
                  data-testid={`broadcast-template-${t.key}`}
                >
                  {t.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Audience */}
          <div>
            <label className="text-sm font-medium block mb-2">Audience</label>
            <Select value={audience} onValueChange={setAudience}>
              <SelectTrigger data-testid="broadcast-audience-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUDIENCES.map((a) => (
                  <SelectItem key={a.value} value={a.value} data-testid={`broadcast-audience-${a.value}`}>
                    {a.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {audience === 'custom' && (
            <div>
              <label className="text-sm font-medium block mb-2">
                Custom recipients (comma, space, or newline separated)
              </label>
              <Textarea
                rows={3}
                placeholder="jane@example.com, john@example.com"
                value={customEmails}
                onChange={(e) => setCustomEmails(e.target.value)}
                data-testid="broadcast-custom-emails"
              />
            </div>
          )}

          {/* Subject */}
          <div>
            <label className="text-sm font-medium block mb-2">Subject line</label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="This month at Christ Kingdom Global Marketplace"
              maxLength={200}
              data-testid="broadcast-subject"
            />
            <p className="text-xs text-muted-foreground mt-1">{subject.length}/200</p>
          </div>

          {/* Body */}
          <div>
            <label className="text-sm font-medium block mb-2">
              Body (HTML supported — use <code>&lt;p&gt;</code>, <code>&lt;a&gt;</code>, <code>&lt;ul&gt;</code>, etc.)
            </label>
            <Textarea
              rows={12}
              value={bodyHtml}
              onChange={(e) => setBodyHtml(e.target.value)}
              placeholder="<p>Hi friend,</p><p>Here's what's new...</p>"
              className="font-mono text-sm"
              data-testid="broadcast-body"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Tip: append <code>?utm_source=email&amp;utm_medium=broadcast&amp;utm_campaign=YOUR_NAME</code> to
              product links so we can measure ROAS in the orders dashboard.
            </p>
          </div>

          {/* Preview */}
          {bodyHtml.trim() && (
            <div>
              <label className="text-sm font-medium block mb-2">Preview</label>
              <div
                className="border border-border rounded-lg p-4 bg-muted/20 prose prose-sm max-w-none"
                data-testid="broadcast-preview"
                // Preview is admin-only; content is admin-authored HTML.
                dangerouslySetInnerHTML={{ __html: bodyHtml }}
              />
            </div>
          )}

          {/* Schedule picker */}
          <div className="border-t pt-5">
            <label className="text-sm font-medium block mb-2 flex items-center gap-2">
              <Clock className="h-4 w-4" /> Schedule for later (optional)
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {presets.map((p) => (
                <Button
                  key={p.label}
                  type="button"
                  variant={scheduleAt === p.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setScheduleAt(scheduleAt === p.value ? '' : p.value)}
                  data-testid={`broadcast-schedule-preset-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {p.label}
                </Button>
              ))}
              {scheduleAt && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setScheduleAt('')}
                  data-testid="broadcast-schedule-clear"
                >
                  <X className="h-3 w-3 mr-1" /> Clear
                </Button>
              )}
            </div>
            <Input
              type="datetime-local"
              value={scheduleAt}
              onChange={(e) => setScheduleAt(e.target.value)}
              min={toLocalInputValue(new Date(Date.now() + 60_000))}
              data-testid="broadcast-schedule-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {scheduleAt
                ? `Will deliver on ${new Date(scheduleAt).toLocaleString()} (your timezone).`
                : 'Leave blank to send immediately. Scheduled sends poll every ~60s.'}
            </p>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSend}
              disabled={sending || !subject.trim() || !bodyHtml.trim()}
              data-testid="broadcast-send-btn"
              size="lg"
            >
              {scheduleAt ? <Clock className="h-4 w-4 mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              {sending
                ? (scheduleAt ? 'Scheduling…' : 'Sending…')
                : (scheduleAt ? 'Schedule broadcast' : 'Send broadcast now')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardHeader>
          <CardTitle>Recent broadcasts</CardTitle>
          <CardDescription>Last 50 broadcasts sent.</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingHistory ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : history.length === 0 ? (
            <div className="text-sm text-muted-foreground" data-testid="broadcast-history-empty">
              No broadcasts sent yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="broadcast-history-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Subject</TableHead>
                    <TableHead>Audience</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Recipients</TableHead>
                    <TableHead>Delivered</TableHead>
                    <TableHead>When</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((b) => {
                    const status = b.status || (typeof b.sent === 'number' ? 'sent' : 'sent');
                    const statusColor = {
                      scheduled: 'bg-amber-100 text-amber-800',
                      sending: 'bg-blue-100 text-blue-800',
                      sent: 'bg-green-100 text-green-800',
                      cancelled: 'bg-gray-100 text-gray-700',
                    }[status] || 'bg-gray-100 text-gray-700';
                    const whenRaw = status === 'scheduled' ? b.send_at : (b.completed_at || b.created_at);
                    return (
                      <TableRow key={b.broadcast_id} data-testid={`broadcast-row-${b.broadcast_id}`}>
                        <TableCell className="font-medium max-w-xs truncate">{b.subject}</TableCell>
                        <TableCell><Badge variant="outline">{b.audience}</Badge></TableCell>
                        <TableCell><Badge className={statusColor}>{status}</Badge></TableCell>
                        <TableCell>{b.recipient_count}</TableCell>
                        <TableCell>
                          {typeof b.sent === 'number' ? (
                            <span className="text-sm">{b.sent} / {b.recipient_count}</span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {whenRaw ? new Date(whenRaw).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell>
                          {status === 'scheduled' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCancel(b.broadcast_id)}
                              data-testid={`broadcast-cancel-${b.broadcast_id}`}
                            >
                              <X className="h-3 w-3 mr-1" /> Cancel
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
